// The Agents page's queue read (#6210) — `GET /v1/agents/queue?agent=<id>`: the pull
// requests and tasks one agent holds right now (queued, running, watching), and on the
// escalation agent's first page the STUCK PRs parked behind them (#6338). Parsed
// through the domain's own `AgentQueueItemWireSchema`, which is strict, so a row
// carrying more of the task than the wire publishes is a loud parse failure here.
//
// LAZY: read only while the agent's queue drawer is open (`open`), never at mount —
// eight cards would otherwise spend eight reads on a drawer nobody opened. PAGED through
// `usePagedQuery`, so the drawer's Load more follows `next_cursor` rather than dropping
// it: an agent can hold more live tasks than one page.

import {
  type AgentId,
  type AgentQueueItemWire,
  AgentQueueItemWireSchema,
} from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { z } from 'zod';
import { t } from '../i18n';
import { activeAccountId } from './active-account';
import { apiFetch, withCursor } from './api';
import { type Page, type PagedList, usePagedQuery } from './paged';
import { SESSION_TOKEN } from './session';
import { since, untilLabel } from './time';

export type { AgentQueueItemWire };

export const AGENT_QUEUE_QUERY_KEY = 'agent_queue';

/** The page envelope, aliased (not inline) so `wire-parse.guard.test.ts` follows the one
 * hop from `{ schema: agentQueuePageSchema }` to `AgentQueueItemWireSchema`. */
const agentQueuePageSchema = z.object({
  data: z.array(AgentQueueItemWireSchema),
  next_cursor: z.string().nullable(),
});

/** One page of `agent`'s queue at `cursor` (null: the first). */
export function fetchAgentQueuePage(
  agent: AgentId,
  cursor: string | null,
  token: string | null,
  signal?: AbortSignal,
): Promise<Page<AgentQueueItemWire>> {
  return apiFetch(
    withCursor(`/v1/agents/queue?agent=${encodeURIComponent(agent)}`, cursor),
    token,
    signal ? { signal } : undefined,
    { schema: agentQueuePageSchema },
  );
}

/**
 * One agent's queue, every page the drawer has asked for. Idle with no session and
 * while `open` is false; keyed by the active org so an org switch re-reads.
 */
export function useAgentQueue(
  agent: AgentId,
  open: () => boolean,
  token: string | null = SESSION_TOKEN,
): PagedList<AgentQueueItemWire> {
  return usePagedQuery((cursor) =>
    createQuery<Page<AgentQueueItemWire>>(() => ({
      queryKey: [AGENT_QUEUE_QUERY_KEY, agent, token, activeAccountId(), cursor()],
      enabled: open() && token !== null,
      queryFn: ({ signal }) => fetchAgentQueuePage(agent, cursor(), token, signal),
    })),
  );
}

/**
 * A stuck row's idle duration — "idle 13h" — on the clock the row re-reads every
 * minute it stays mounted. `null` (no idle clock could be computed) says so in
 * words rather than a blank. `now` is the seam a test pins.
 */
export function idleFor(idleSinceIso: string | null, now: number = Date.now()): string {
  if (idleSinceIso === null) return t('agents.queue.stuck.idle_none');
  return t('agents.queue.stuck.idle', { for: since(idleSinceIso, now) });
}

/**
 * A stuck row's rescue countdown (#6338): "Rescue hands over in 3h", or "Due: ..."
 * once `rescueDueAtIso` has passed — the rescue fires on the PR's next
 * re-observation, not on the instant, so a past time is due rather than late.
 * `now` is the seam a test pins.
 */
export function rescueCountdown(rescueDueAtIso: string, now: number = Date.now()): string {
  if (Date.parse(rescueDueAtIso) <= now) return t('agents.queue.stuck.rescue_due');
  return t('agents.queue.stuck.rescue_in', { until: untilLabel(rescueDueAtIso, now) });
}
